import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, catchError, of } from 'rxjs';
import { UsersService } from '@app/api/user-service/api/users.service';
import { UserBusinessUnitsService } from
  '@app/api/user-service/api/userBusinessUnits.service';
import { KeycloakUserSearchRequest } from
  '@app/api/user-service/model/keycloakUserSearchRequest';
import { KeycloakUserResponse } from
  '@app/api/user-service/model/keycloakUserResponse';
import { UserBusinessUnitAssignRequest } from
  '@app/api/user-service/model/userBusinessUnitAssignRequest';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';

@Component({
  selector: 'app-assign-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ProgressSpinnerModule,
    TagModule,
    SearchFormComponent,
    AppButtonComponent,
  ],
  templateUrl: './assign-user-dialog.component.html',
  styleUrl: './assign-user-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignUserDialogComponent implements AfterViewInit {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  @ViewChild(SearchFormComponent) searchFormRef?: SearchFormComponent;
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly usersService = inject(UsersService);
  private readonly userBusinessUnitsService = inject(
    UserBusinessUnitsService,
  );

  /** Each field 50% minus half of gap so 2 fields fill the row width */
  readonly searchFormConfig: FormFieldConfig[] = [
    {
      key: 'firstName',
      label: 'configuration.business_unit.dialog.assign_user.first_name',
      type: 'text',
      width: 'calc(50% - 6px)',
    },
    {
      key: 'lastName',
      label: 'configuration.business_unit.dialog.assign_user.last_name',
      type: 'text',
      width: 'calc(50% - 6px)',
    },
    {
      key: 'username',
      label: 'configuration.business_unit.dialog.assign_user.username',
      type: 'text',
      width: 'calc(50% - 6px)',
    },
    {
      key: 'email',
      label: 'configuration.business_unit.dialog.assign_user.email',
      type: 'text',
      width: 'calc(50% - 6px)',
    },
  ];

  readonly searching = signal(false);
  readonly assigning = signal(false);
  readonly searchResults = signal<KeycloakUserResponse[]>([]);
  readonly selectedUser = signal<KeycloakUserResponse | null>(null);
  /** User ids/emails assigned in this dialog session; used to disable them in current results */
  private readonly justAssignedInSession = signal<Set<string>>(new Set());
  private assignedSuccess = false;

  businessUnitId: number;
  private readonly existingUserIds: Set<string>;
  private readonly existingUserEmails: Set<string>;
  private readonly onAssignSuccess?: () => void;

  constructor() {
    const data = this.config.data ?? {};
    this.businessUnitId = data.businessUnitId ?? 0;
    this.existingUserIds = new Set((data.existingUserIds as string[]) ?? []);
    this.existingUserEmails = new Set(
      (data.existingUserEmails as string[]) ?? [],
    );
    this.onAssignSuccess = data.onAssignSuccess as (() => void) | undefined;
  }

  isAlreadyAssigned(user: KeycloakUserResponse): boolean {
    const session = this.justAssignedInSession();
    if (user.id && (this.existingUserIds.has(user.id) || session.has(user.id))) {
      return true;
    }
    const email = user.email?.trim();
    return !!(
      email &&
      (this.existingUserEmails.has(email) || session.has(email))
    );
  }

  ngAfterViewInit(): void {
    this.patchCloseToClearAndPassResult();
  }

  private patchCloseToClearAndPassResult(): void {
    const ref = this.dialogRef as DynamicDialogRef & { close: (result?: unknown) => void };
    const originalClose = ref.close.bind(ref);
    ref.close = (result?: unknown) => {
      this.searchFormRef?.onReset();
      const value =
        result === undefined ? { success: this.assignedSuccess } : result;
      originalClose(value);
    };
  }

  onSearch(formData: SearchFormData): void {
    const firstName = this.toStr(formData['firstName']);
    const lastName = this.toStr(formData['lastName']);
    const username = this.toStr(formData['username']);
    const email = this.toStr(formData['email']);

    const allEmpty =
      !firstName && !lastName && !username && !email;

    if (allEmpty) {
      this.searchResults.set([]);
      this.selectedUser.set(null);
      return;
    }

    const request: KeycloakUserSearchRequest = {
      page: 0,
      size: 20,
      firstName,
      lastName,
      username,
      email,
    };

    this.searching.set(true);
    this.selectedUser.set(null);

    this.usersService
      .searchUsers(request, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        finalize(() => this.searching.set(false)),
        catchError((err: unknown) => {
          console.error('[AssignUserDialogComponent] Search failed:', err);
          return of({
            pageContent: [],
            totalElements: 0,
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        const list = page?.pageContent ?? [];
        this.searchResults.set(Array.isArray(list) ? list : []);
      });
  }

  selectUser(user: KeycloakUserResponse): void {
    if (this.isAlreadyAssigned(user)) return;
    this.selectedUser.set(
      this.selectedUser()?.id === user.id ? null : user,
    );
  }

  onAssign(): void {
    const user = this.selectedUser();
    if (!user || !this.businessUnitId) {
      return;
    }

    const body: UserBusinessUnitAssignRequest = {
      businessUnitId: this.businessUnitId,
      userId: user.id ?? undefined,
      email: user.email ?? undefined,
    };

    this.assigning.set(true);

    this.userBusinessUnitsService
      .assignUserToBusinessUnit(body, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        finalize(() => this.assigning.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.assignedSuccess = true;
          this.addToJustAssignedInSession(user);
          this.selectedUser.set(null);
          this.onAssignSuccess?.();
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.messages.success'),
            detail: this.translate.instant(
              'configuration.business_unit.dialog.assign_user_success',
            ),
          });
        },
        error: (err: unknown) => {
          console.error(
            '[AssignUserDialogComponent] Assign failed:',
            err,
          );
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant(
              'configuration.business_unit.dialog.assign_user_failed',
            ),
          });
        },
      });
  }

  onCancel(): void {
    this.dialogRef.close({ success: this.assignedSuccess });
  }

  isSelected(user: KeycloakUserResponse): boolean {
    return this.selectedUser()?.id === user.id;
  }

  onSearchReset(): void {
    this.searchResults.set([]);
    this.selectedUser.set(null);
  }

  private addToJustAssignedInSession(user: KeycloakUserResponse): void {
    const next = new Set(this.justAssignedInSession());
    if (user.id) next.add(user.id);
    const email = user.email?.trim();
    if (email) next.add(email);
    this.justAssignedInSession.set(next);
  }

  private toStr(v: unknown): string | undefined {
    if (v == null) return undefined;
    if (typeof v === 'string') return v.trim() || undefined;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return undefined;
  }
}
